import { AlertTriangle, Loader2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import type { Case } from '@/services/api'

export interface DeleteCaseDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  caseData: Case | null
  onConfirm: (caseData: Case) => void
  deleting: boolean
}

export function DeleteCaseDialog({
  open,
  onOpenChange,
  caseData,
  onConfirm,
  deleting,
}: DeleteCaseDialogProps) {
  const { t } = useTranslation()
  const [confirmName, setConfirmName] = useState('')

  const handleDelete = () => {
    if (!caseData || confirmName !== caseData.name) return
    onConfirm(caseData)
  }

  return (
    <Dialog
      open={open}
      onOpenChange={isOpen => {
        onOpenChange(isOpen)
        if (!isOpen) setConfirmName('')
      }}
    >
      <DialogContent className="bg-card border-border">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 rounded-lg bg-destructive/20">
              <AlertTriangle className="h-5 w-5 text-destructive" />
            </div>
            <DialogTitle className="text-foreground">{t('cases.delete.title')}</DialogTitle>
          </div>
          <DialogDescription className="text-muted-foreground">
            {t('cases.delete.description')}
            <span className="font-semibold text-foreground"> {caseData?.name}</span>{' '}
            {t('cases.delete.descriptionSuffix')}
          </DialogDescription>
        </DialogHeader>
        <div className="py-4 space-y-4">
          <ul className="list-disc list-outside pl-5 text-sm text-muted-foreground space-y-1 md:list-inside md:pl-0">
            <li>{t('cases.delete.bullet1')}</li>
            <li>{t('cases.delete.bullet2')}</li>
            <li>{t('cases.delete.bullet3')}</li>
            <li>{t('cases.delete.bullet4')}</li>
          </ul>

          <div className="space-y-2">
            {/* Below md the label flows as one sentence instead of three squeezed flex columns. */}
            <Label
              htmlFor="confirm-name"
              className="text-foreground max-md:block max-md:leading-snug"
            >
              {t('cases.delete.typeToConfirm')}{' '}
              <span className="font-mono text-destructive">{caseData?.name}</span>{' '}
              {t('cases.delete.typeToConfirmSuffix')}
            </Label>
            <Input
              id="confirm-name"
              placeholder={t('cases.delete.placeholder')}
              value={confirmName}
              onChange={e => setConfirmName(e.target.value)}
              className="bg-muted border-border text-foreground placeholder:text-muted-foreground"
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="border-border text-foreground hover:bg-accent"
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleDelete}
            disabled={deleting || confirmName !== caseData?.name}
            className="bg-destructive text-foreground hover:bg-destructive/90 disabled:opacity-50"
          >
            {deleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('cases.delete.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
